/**
 * useGoogleAds Composable
 * Manages Google AdSense/DFP ad loading with proper error handling
 * Supports multiple review types, layouts, and ad configurations
 */

import { ref, nextTick, inject } from 'vue'
import googleAdsConfig from '@/config/googleAds.config.js'

export function useGoogleAds() {
  // State
  const gptInitialized = ref(false)
  const gptLoadFailed = ref(false)
  const gptLoadTimeout = ref(false)
  const adBlockerDetected = ref(false)
  const adLoadErrors = ref({})
  const definedSlots = new Set()

  // Inject noAdsPage flag
  const noAdsPage = inject('noAdsPage', false)

  /**
   * Detect ad blockers
   */
  const detectAdBlocker = () => {
    const adTest = document.createElement('div')
    adTest.innerHTML = '&nbsp;'
    adTest.className = 'adsbox'
    adTest.style.position = 'absolute'
    adTest.style.left = '-10000px'
    document.body.appendChild(adTest)

    setTimeout(() => {
      if (adTest.offsetHeight === 0) {
        adBlockerDetected.value = true
        console.warn('[Google Ads] Ad blocker detected')
        markAllAdContainersAsFailed()
      }
      document.body.removeChild(adTest)
    }, 100)
  }

  /**
   * Initialize Google Publisher Tag (GPT)
   */
  const initializeGPT = () => {
    if (gptInitialized.value || gptLoadFailed.value || noAdsPage) return

    console.log('[Google Ads] Initializing GPT...')

    // Set timeout for GPT loading
    const timeoutId = setTimeout(() => {
      console.warn('[Google Ads] GPT loading timeout')
      gptLoadTimeout.value = true
      markAllAdContainersAsFailed()
    }, googleAdsConfig.gptSettings.loadTimeout)

    // Create GPT script element
    const gptScript = document.createElement('script')
    gptScript.async = true
    gptScript.src = googleAdsConfig.gptSettings.scriptUrl

    gptScript.onload = () => {
      clearTimeout(timeoutId)
      window.googletag = window.googletag || { cmd: [] }
      window.googletag.cmd.push(function() {
        try {
          window.googletag.pubads().enableAsyncRendering()
          window.googletag.pubads().collapseEmptyDivs(true)
          window.googletag.pubads().enableSingleRequest()

          // Add slot render ended event listener
          window.googletag.pubads().addEventListener('slotRenderEnded', function(event) {
            const slotId = event.slot.getSlotElementId()
            const container = document.getElementById(slotId)

            if (!container) return

            // Clear any pending timeout for this slot
            if (container._adTimeoutId) {
              clearTimeout(container._adTimeoutId)
              delete container._adTimeoutId
            }

            if (event.isEmpty) {
              // Slot rendered but no ad was returned
              container.classList.add('ad-failed')
              container.classList.remove('ad-loaded')
              container.setAttribute('data-ad-status', 'failed')
              container.setAttribute('data-ad-reason', 'empty')
              console.log(`[Google Ads] Slot ${slotId} rendered empty (no ad from Google)`)
            } else {
              // Ad was successfully rendered
              container.classList.add('ad-loaded')
              container.classList.remove('ad-failed')
              container.setAttribute('data-ad-status', 'loaded')
              container.setAttribute('data-ad-size', event.size.join('x'))
              console.log(`[Google Ads] Slot ${slotId} loaded successfully (${event.size.join('x')})`)
            }
          })

          window.googletag.enableServices()
          gptInitialized.value = true
          console.log('[Google Ads] GPT initialized successfully')
        } catch (error) {
          console.error('[Google Ads] GPT initialization error:', error)
          gptLoadFailed.value = true
        }
      })
    }

    gptScript.onerror = () => {
      clearTimeout(timeoutId)
      console.error('[Google Ads] Failed to load GPT script')
      gptLoadFailed.value = true
      markAllAdContainersAsFailed()
    }

    document.head.appendChild(gptScript)
  }

  /**
   * Mark all ad containers as failed
   */
  const markAllAdContainersAsFailed = () => {
    const containers = document.querySelectorAll('.review-top-banner, .inner-banner, .comment-banner')
    containers.forEach(container => {
      if (container && !container.hasChildNodes()) {
        container.classList.add('ad-failed')
        container.setAttribute('data-ad-status', 'failed')
        container.style.display = 'none'
      }
    })
  }

  /**
   * Mark individual container as failed
   */
  const markAdContainerFailed = (slotId, reason) => {
    const container = document.getElementById(slotId)
    if (container) {
      container.classList.add('ad-failed')
      container.setAttribute('data-ad-status', 'failed')
      container.setAttribute('data-ad-error', reason)
      console.warn(`[Google Ads] Container ${slotId} failed: ${reason}`)
      container.style.display = 'none'
    }
    adLoadErrors.value[slotId] = reason
  }

  /**
   * Create size mapping
   */
  const createSizeMapping = (mappingType) => {
    if (!window.googletag || !window.googletag.sizeMapping) {
      return null
    }

    const mappingConfig = googleAdsConfig.sizeMappings[mappingType]
    if (!mappingConfig) {
      console.warn(`[Google Ads] Unknown mapping type: ${mappingType}`)
      return null
    }

    let mapping = window.googletag.sizeMapping()
    mappingConfig.forEach(config => {
      mapping = mapping.addSize(config.viewport, config.sizes)
    })
    return mapping.build()
  }

  /**
   * Build full ad unit path
   */
  const buildAdUnitPath = (adUnitName) => {
    return `/${googleAdsConfig.networkCode}/${adUnitName}`
  }

  /**
   * Define ad slot with timeout protection
   */
  const defineAdSlot = (slotId, adUnitPath, sizes, mapping = null) => {
    // Prevent duplicate definitions
    if (definedSlots.has(slotId)) {
      console.warn(`[Google Ads] Slot ${slotId} already defined, skipping`)
      return true
    }

    if (adBlockerDetected.value) {
      markAdContainerFailed(slotId, 'Ad blocker detected')
      return false
    }

    if (!window.googletag || !window.googletag.cmd) {
      markAdContainerFailed(slotId, 'GPT not initialized')
      return false
    }

    // Set timeout to mark as failed if no ad renders in 5 seconds
    const container = document.getElementById(slotId)
    const timeoutId = setTimeout(() => {
      if (container && !container.hasAttribute('data-ad-status')) {
        console.warn(`[Google Ads] Slot ${slotId} timeout - no ad rendered`)
        markAdContainerFailed(slotId, 'Render timeout')
      }
    }, 5000)

    // Store timeout ID on container so slotRenderEnded can clear it
    if (container) {
      container._adTimeoutId = timeoutId
    }

    try {
      window.googletag.cmd.push(function() {
        try {
          const slot = window.googletag.defineSlot(adUnitPath, sizes, slotId)
          if (!slot) {
            if (container && container._adTimeoutId) {
              clearTimeout(container._adTimeoutId)
              delete container._adTimeoutId
            }
            markAdContainerFailed(slotId, 'Failed to define ad slot')
            return
          }
          if (mapping) {
            slot.defineSizeMapping(mapping)
          }
          slot.addService(window.googletag.pubads())
          definedSlots.add(slotId)

          // Timeout will be cleared by slotRenderEnded event
        } catch (error) {
          if (container && container._adTimeoutId) {
            clearTimeout(container._adTimeoutId)
            delete container._adTimeoutId
          }
          markAdContainerFailed(slotId, `Define error: ${error.message}`)
        }
      })
      return true
    } catch (error) {
      if (container && container._adTimeoutId) {
        clearTimeout(container._adTimeoutId)
        delete container._adTimeoutId
      }
      markAdContainerFailed(slotId, `Define error: ${error.message}`)
      return false
    }
  }

  /**
   * Display ad in container
   */
  const displayAd = (slotId) => {
    if (!window.googletag || !window.googletag.cmd) {
      markAdContainerFailed(slotId, 'GPT not initialized')
      return false
    }

    try {
      window.googletag.cmd.push(function() {
        try {
          window.googletag.display(slotId)
        } catch (error) {
          markAdContainerFailed(slotId, `Display error: ${error.message}`)
        }
      })
      return true
    } catch (error) {
      markAdContainerFailed(slotId, `Display error: ${error.message}`)
      return false
    }
  }

  /**
   * Load top banner ad
   */
  const loadTopBannerAd = async (id, isFirstReview = false) => {
    await nextTick()

    const slotId = googleAdsConfig.containerIds.topBanner(id)
    const container = document.getElementById(slotId)

    if (!container) {
      console.warn('[Google Ads] Top banner container not found:', slotId)
      return false
    }

    if (gptLoadFailed.value || gptLoadTimeout.value) {
      markAdContainerFailed(slotId, 'GPT unavailable')
      return false
    }

    try {
      const adUnitName = isFirstReview
        ? googleAdsConfig.adUnits.firstReview.top
        : googleAdsConfig.adUnits.secondReview.top

      const adUnitPath = buildAdUnitPath(adUnitName)
      const mapping = createSizeMapping('topBanner')
      const sizes = googleAdsConfig.adSizes.topBanner

      const defined = defineAdSlot(slotId, adUnitPath, sizes, mapping)
      if (defined) {
        displayAd(slotId)
        container.setAttribute('data-ad-status', 'requested')
        return true
      }
      return false
    } catch (error) {
      markAdContainerFailed(slotId, `Load error: ${error.message}`)
      return false
    }
  }

  /**
   * Load content ads for text reviews (middle and bottom)
   */
  const loadTextReviewAds = async (id, isFirstReview, adsLayout) => {
    await nextTick()

    const adUnitBase = isFirstReview
      ? googleAdsConfig.adUnits.firstReview
      : googleAdsConfig.adUnits.secondReview

    const results = {
      middleOne: false,
      bottom: false
    }

    try {
      // Middle banner (conditional on layout)
      const showMiddleOne = isFirstReview || adsLayout === googleAdsConfig.layouts.LAYOUT_1A

      if (showMiddleOne) {
        const middleSlotId = googleAdsConfig.containerIds.middleOne(id)
        const middleContainer = document.getElementById(middleSlotId)

        if (middleContainer) {
          const adUnitPath = buildAdUnitPath(adUnitBase.contentInner)
          const mapping = createSizeMapping('contentInner')
          const sizes = googleAdsConfig.adSizes.contentInner

          if (defineAdSlot(middleSlotId, adUnitPath, sizes, mapping)) {
            displayAd(middleSlotId)
            results.middleOne = true
          }
        }
      }

      // Bottom banner (always shown)
      const bottomSlotId = googleAdsConfig.containerIds.bottom(id)
      const bottomContainer = document.getElementById(bottomSlotId)

      if (bottomContainer) {
        const adUnitPath = buildAdUnitPath(adUnitBase.underContent)
        const mapping = createSizeMapping('underContent')
        const sizes = googleAdsConfig.adSizes.underContent

        if (defineAdSlot(bottomSlotId, adUnitPath, sizes, mapping)) {
          displayAd(bottomSlotId)
          results.bottom = true
        }
      }

      return results
    } catch (error) {
      console.error('[Google Ads] Error loading text review ads:', error)
      return results
    }
  }

  /**
   * Load content ads for image reviews
   */
  const loadImageReviewAds = async (id, isFirstReview) => {
    await nextTick()

    const adUnitBase = isFirstReview
      ? googleAdsConfig.adUnits.firstReview
      : googleAdsConfig.adUnits.secondReview

    const results = {
      images: [],
      bottom: false
    }

    try {
      // Ads between images (0-4)
      for (let i = 0; i < 5; i++) {
        const imageSlotId = `${googleAdsConfig.containerIds.imagesPrefix}${i}-${id}`
        const container = document.getElementById(imageSlotId)

        if (container) {
          const adUnitPath = buildAdUnitPath(adUnitBase.contentInner)
          const mapping = createSizeMapping('contentInner')
          const sizes = googleAdsConfig.adSizes.contentInner

          if (defineAdSlot(imageSlotId, adUnitPath, sizes, mapping)) {
            displayAd(imageSlotId)
            container.classList.add('images-review-innerbanner-filled')
            results.images.push(i)
          }
        }
      }

      // Bottom banner for image reviews
      const bottomSlotId = googleAdsConfig.containerIds.imagesBottom(id)
      const bottomContainer = document.getElementById(bottomSlotId)

      if (bottomContainer) {
        const adUnitPath = buildAdUnitPath(adUnitBase.underContent)
        const mapping = createSizeMapping('underContent')
        const sizes = googleAdsConfig.adSizes.underContent

        if (defineAdSlot(bottomSlotId, adUnitPath, sizes, mapping)) {
          displayAd(bottomSlotId)
          results.bottom = true
        }
      }

      return results
    } catch (error) {
      console.error('[Google Ads] Error loading image review ads:', error)
      return results
    }
  }

  /**
   * Load comment banner ad
   */
  const loadCommentBannerAd = async (commentRelation) => {
    await nextTick()

    const slotId = googleAdsConfig.containerIds.commentBanner(commentRelation)
    const container = document.getElementById(slotId)

    if (!container) {
      console.warn('[Google Ads] Comment banner container not found:', slotId)
      return false
    }

    if (gptLoadFailed.value || gptLoadTimeout.value) {
      markAdContainerFailed(slotId, 'GPT unavailable')
      return false
    }

    try {
      const adUnitPath = buildAdUnitPath(googleAdsConfig.adUnits.firstReview.comments)
      const mapping = createSizeMapping('comments')
      const sizes = googleAdsConfig.adSizes.comments

      const defined = defineAdSlot(slotId, adUnitPath, sizes, mapping)
      if (defined) {
        displayAd(slotId)
        return true
      }
      return false
    } catch (error) {
      markAdContainerFailed(slotId, `Load error: ${error.message}`)
      return false
    }
  }

  /**
   * Load ads for first review
   */
  const loadFirstReviewAds = async (reviewData) => {
    if (!reviewData || !reviewData.main || !reviewData.main[0]) return

    const id = reviewData.main[0].ID
    const submType = reviewData.main[0].submType
    const reviewType = submType == 0 ? googleAdsConfig.reviewTypes.TEXT : googleAdsConfig.reviewTypes.IMAGES

    // Initialize GPT if needed
    if (!gptInitialized.value) {
      initializeGPT()
      await new Promise(resolve => setTimeout(resolve, 200))

      if (!gptInitialized.value) {
        console.warn('[Google Ads] GPT not ready, skipping first review ads')
        return
      }
    }

    try {
      // Load top banner
      await loadTopBannerAd(id, true)

      // Load content ads based on review type
      if (reviewType === googleAdsConfig.reviewTypes.TEXT) {
        await loadTextReviewAds(id, true, reviewData.main[0].ads_layout)
      } else {
        await loadImageReviewAds(id, true)
      }
    } catch (error) {
      console.error('[Google Ads] Error loading first review ads:', error)
    }
  }

  /**
   * Load ads for second or subsequent reviews
   */
  const loadSubsequentReviewAds = async (reviewData, adsLayout) => {
    if (!reviewData || !reviewData.main || !reviewData.main[0]) return

    const id = reviewData.main[0].ID
    const submType = reviewData.main[0].submType
    const reviewType = submType == 0 ? googleAdsConfig.reviewTypes.TEXT : googleAdsConfig.reviewTypes.IMAGES

    // Check if GPT failed
    if (gptLoadFailed.value || gptLoadTimeout.value) {
      console.warn('[Google Ads] Skipping ads due to GPT failure')
      return
    }

    try {
      // Load top banner
      await loadTopBannerAd(id, false)

      // Load content ads based on review type and layout
      if (reviewType === googleAdsConfig.reviewTypes.TEXT) {
        // Determine if we should show middle banner based on layout
        const showMiddleOne = adsLayout === googleAdsConfig.layouts.LAYOUT_1A

        if (showMiddleOne) {
          await loadTextReviewAds(id, false, adsLayout)
        } else {
          // Layout 2: no middle ad, only bottom
          await loadTextReviewAds(id, false, googleAdsConfig.layouts.LAYOUT_2)
        }
      } else {
        await loadImageReviewAds(id, false)
      }
    } catch (error) {
      console.error('[Google Ads] Error loading subsequent review ads:', error)
      // Mark containers as failed
      markAdContainerFailed(googleAdsConfig.containerIds.topBanner(id), 'Load exception')
      markAdContainerFailed(googleAdsConfig.containerIds.bottom(id), 'Load exception')
    }
  }

  /**
   * Load ads for a review (main entry point)
   */
  const loadAdsForReview = async (reviewData, index, totalReviews) => {
    if (noAdsPage || !reviewData || !reviewData.main || !reviewData.main[0]) return

    const id = reviewData.main[0].ID
    const isFirstReview = index === 0
    const adsLayout = reviewData.main[0].ads_layout

    // Skip if review has ads disabled
    if (reviewData.main[0].no_cost == 1 || reviewData.main[0].IDnum) return

    // Check if GPT failed to load
    if (gptLoadFailed.value || gptLoadTimeout.value) {
      console.warn('[Google Ads] Skipping ads due to GPT failure')
      return
    }

    // Initialize GPT if not already done
    if (!gptInitialized.value) {
      initializeGPT()
      await new Promise(resolve => setTimeout(resolve, 200))

      if (!gptInitialized.value) {
        console.warn('[Google Ads] GPT not ready, skipping ads for this review')
        return
      }
    }

    try {
      if (isFirstReview) {
        await loadFirstReviewAds(reviewData)
      } else {
        await loadSubsequentReviewAds(reviewData, adsLayout)
      }
    } catch (error) {
      console.error('[Google Ads] Error loading ads for review', id, ':', error)
    }
  }

  /**
   * Load ads for comments when they enter viewport
   */
  const loadCommentAdsOnScroll = (commentRelations) => {
    if (!commentRelations || commentRelations.length === 0) return

    if (gptLoadFailed.value || gptLoadTimeout.value || adBlockerDetected.value) {
      return
    }

    for (let commentRelation of commentRelations) {
      const commentCluster = document.getElementById(`commentsClusterBlck-${commentRelation}`)
      if (commentCluster && !commentCluster.getAttribute('data-ads-loaded')) {
        const rect = commentCluster.getBoundingClientRect()
        if (rect.top < window.innerHeight && rect.bottom > 0) {
          loadCommentBannerAd(commentRelation)
          commentCluster.setAttribute('data-ads-loaded', 'true')
        }
      }
    }
  }

  // Return public API
  return {
    // State
    gptInitialized,
    gptLoadFailed,
    gptLoadTimeout,
    adBlockerDetected,
    adLoadErrors,

    // Methods
    detectAdBlocker,
    initializeGPT,
    loadAdsForReview,
    loadCommentAdsOnScroll,

    // Config access
    googleAdsConfig
  }
}
